package com.arenaproplay.admin.ui.components.charts.svg

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * Night Arena vertical bar columns chart — gradient fill, empty-bucket 3px stub.
 * Suitable for hour/day distributions. Ported from kontaq stackedBarChartSvg
 * (SimpleTabs.kt) adapted to Night Arena single-series columns.
 *
 * @param buckets    data buckets with label and value
 * @param colorHex   bar gradient base color
 * @param heightPx   SVG height in pixels
 * @param modifier   standard Kobweb modifier
 */
@Composable
fun BarColumns(
    buckets: List<BarBucket>,
    colorHex: String = "#0061FF",
    heightPx: Int = 120,
    modifier: Modifier = Modifier,
) {
    if (buckets.isEmpty()) {
        // L4: apply modifier to root element (same as AreaChart)
        Div(attrs = modifier.toAttrs {
            style {
                property("height", "${heightPx}px")
                property("background", "rgba(255,255,255,.02)")
                property("border-radius", "8px")
                property("width", "100%")
            }
        })
        return
    }
    val svg = buildBarColumnsSvg(buckets, colorHex, heightPx)
    // L4: apply modifier to root element
    Div(attrs = modifier.toAttrs {
        style {
            property("width", "100%")
            property("line-height", "0")
        }
        ref { el ->
            el.innerHTML = svg
            onDispose { el.innerHTML = "" }
        }
    })
}

// ── SVG string builder ─────────────────────────────────────────────────────

private fun buildBarColumnsSvg(buckets: List<BarBucket>, colorHex: String, heightPx: Int): String {
    val n = buckets.size
    val w = 600.0
    val h = heightPx.toDouble()
    val padL = 4.0
    val padR = 8.0
    val padT = 8.0
    val padB = 20.0
    val baseY = h - padB

    val max = buckets.maxOf { it.value.coerceAtLeast(0.0) }.coerceAtLeast(1.0)
    val slot = (w - padL - padR) / n
    val barW = (slot * 0.55).coerceAtMost(28.0).coerceAtLeast(3.0)
    val labelEvery = when {
        n <= 12 -> 1
        n <= 24 -> 2
        else -> (n + 7) / 8
    }

    // Gradient id — deterministic, based on color hash
    val gradId = "nabcg${kotlin.math.abs(colorHex.hashCode())}"

    return buildString {
        append("<svg viewBox=\"0 0 ${w.toInt()} ${h.toInt()}\" width=\"100%\"")
        append(" xmlns=\"http://www.w3.org/2000/svg\" style=\"display:block\">")

        // Gradient def
        append("<defs>")
        append("<linearGradient id=\"$gradId\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\">")
        append("<stop offset=\"0\" stop-color=\"$colorHex\" stop-opacity=\"0.85\"/>")
        append("<stop offset=\"1\" stop-color=\"$colorHex\" stop-opacity=\"0.35\"/>")
        append("</linearGradient>")
        append("</defs>")

        // Baseline
        append("<line x1=\"${fmt(padL)}\" y1=\"${fmt(baseY)}\"")
        append(" x2=\"${fmt(w - padR + 4)}\" y2=\"${fmt(baseY)}\"")
        append(" stroke=\"rgba(255,255,255,.08)\" stroke-width=\"1\"/>")

        buckets.forEachIndexed { i, bucket ->
            val cx = padL + slot * i + slot / 2
            val v = bucket.value.coerceAtLeast(0.0)
            val tooltip = xmlEsc("${bucket.label}: ${fmt(v)}")

            if (v <= 0.0) {
                // Empty bucket — 3px stub per spec
                append("<rect x=\"${fmt(cx - barW / 2)}\" y=\"${fmt(baseY - 3)}\"")
                append(" width=\"${fmt(barW)}\" height=\"3\" rx=\"1.5\"")
                append(" fill=\"rgba(255,255,255,.06)\">")
                append("<title>$tooltip</title></rect>")
            } else {
                val barH = ((baseY - padT) * v / max).coerceAtLeast(4.0)
                val rx = minOf(3.0, barW / 2, barH / 2)
                append("<rect x=\"${fmt(cx - barW / 2)}\" y=\"${fmt(baseY - barH)}\"")
                append(" width=\"${fmt(barW)}\" height=\"${fmt(barH)}\" rx=\"${fmt(rx)}\"")
                append(" fill=\"url(#$gradId)\">")
                append("<title>$tooltip</title></rect>")
                // Value label above bar when buckets are sparse enough to read
                if (n <= 12) {
                    append("<text x=\"${fmt(cx)}\" y=\"${fmt(baseY - barH - 4)}\"")
                    append(" text-anchor=\"middle\" font-size=\"9\" font-weight=\"600\"")
                    append(" fill=\"rgba(240,244,252,.55)\"")
                    append(" font-family=\"'JetBrains Mono',monospace\">")
                    append(fmt(v))
                    append("</text>")
                }
            }

            // X-axis label (sparse)
            if (i % labelEvery == 0 || i == n - 1) {
                append("<text x=\"${fmt(cx)}\" y=\"${fmt(h - 4)}\"")
                append(" text-anchor=\"middle\" font-size=\"9.5\"")
                append(" fill=\"rgba(240,244,252,.35)\"")
                append(" font-family=\"'JetBrains Mono',monospace\">")
                append(xmlEsc(bucket.label))
                append("</text>")
            }
        }

        append("</svg>")
    }
}
